import React, { useState, useRef, useEffect } from "react";
import { UseFormRegister, FieldError, UseFormTrigger } from "react-hook-form";
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

interface DropdownProps {
  label?: string;
  error?: FieldError;
  register?: UseFormRegister<any>;
  name?: string;
  options: Option[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  onBlur?: () => void;
  disabled?: boolean;
  searchable?: boolean;
  placeholder?: string;
  containerClassName?: string;
  multiple?: boolean;
  trigger?: UseFormTrigger<any>;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  error,
  register,
  name,
  options,
  value,
  onChange,
  onBlur,
  disabled = false,
  searchable = false,
  placeholder = "Select an option",
  containerClassName = "",
  multiple = false,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasTriggeredBlurRef = useRef(false);

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options;

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const selectedOptions = selectedValues
    .map((val) => options.find((o) => o.value === val))
    .filter((o): o is Option => o !== undefined);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          setIsOpen(false);
          hasTriggeredBlurRef.current = true;
          // Trigger onBlur when dropdown closes after a delay to ensure state is updated
          setTimeout(() => {
            onBlur?.();
            hasTriggeredBlurRef.current = false;
          }, 0);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onBlur]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ": {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        break;
      }
      case "ArrowDown":
      case "ArrowUp": {
        // Only support arrow navigation for single-select for now
        if (multiple) return;
        e.preventDefault();

        const currentValue = Array.isArray(value) ? value?.[0] : value;
        const enabledOptions = filteredOptions.filter((o) => !o.disabled);

        if (enabledOptions.length === 0) return;

        const currentIndex = enabledOptions.findIndex(
          (o) => o.value === currentValue,
        );

        const direction = e.key === "ArrowDown" ? 1 : -1;
        let nextIndex = currentIndex;

        // If nothing selected yet, start from beginning/end
        if (currentIndex === -1) {
          nextIndex = direction === 1 ? 0 : enabledOptions.length - 1;
        } else {
          nextIndex = currentIndex + direction;
          if (nextIndex < 0) nextIndex = enabledOptions.length - 1;
          if (nextIndex >= enabledOptions.length) nextIndex = 0;
        }

        const nextOption = enabledOptions[nextIndex];
        if (nextOption) {
          // Update value without closing dropdown so user can continue navigating
          onChange?.(nextOption.value);
          // Validation is handled by setValue with shouldValidate: true in parent component
          setIsOpen(true);
        }
        break;
      }
      case "Escape": {
        if (isOpen) {
          e.stopPropagation();
          setIsOpen(false);
        }
        break;
      }
      default:
        break;
    }
  };

  const handleSelect = (option: Option) => {
    if (option.disabled) return;

    if (multiple) {
      let newValues: string[];
      if (selectedValues.includes(option.value)) {
        newValues = selectedValues.filter((v) => v !== option.value);
      } else {
        newValues = [...selectedValues, option.value];
      }
      onChange?.(newValues);
      // Validation is handled by setValue with shouldValidate: true in parent component
      // Do NOT manually trigger here to avoid race conditions
    } else {
      onChange?.(option.value);
      setIsOpen(false);
      hasTriggeredBlurRef.current = true;
      // Trigger onBlur when selection is made and dropdown closes
      setTimeout(() => {
        onBlur?.();
        hasTriggeredBlurRef.current = false;
      }, 0);
      // Validation is handled by setValue with shouldValidate: true in parent component
      // Do NOT manually trigger here to avoid race conditions
    }
    setSearchTerm("");
  };

  const handleRemove = (val: string) => {
    if (!multiple) return;
    const newValues = selectedValues.filter((v) => v !== val);
    onChange?.(newValues);
    // Validation is handled by setValue with shouldValidate: true in parent component
    // Do NOT manually trigger here to avoid race conditions
  };

  const dropdownClasses = `
    relative w-full
    ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
  `;

  const triggerClasses = `
    block w-full rounded-lg border-2
    bg-white px-4 py-2 text-gray-900
    placeholder-gray-400
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
    shadow-sm hover:shadow-md
    ${
      error
        ? "border-danger-default focus:border-danger-default focus:ring-danger-default"
        : "border-gray-200 focus:border-primary-default focus:ring-primary-default"
    }
    ${disabled ? "cursor-not-allowed" : ""}
  `;

  return (
    <div className={`space-y-1.5 ${containerClassName}`}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-secondary-700"
        >
          {label}
        </label>
      )}
      <div className={dropdownClasses} ref={dropdownRef}>
        <div
          className={triggerClasses}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={label || placeholder}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleTriggerKeyDown}
          onBlur={(e) => {
            // Only trigger onBlur if focus is moving outside the dropdown and we haven't already triggered it
            // Use setTimeout to check if focus moved to dropdown options
            setTimeout(() => {
              if (
                !dropdownRef.current?.contains(document.activeElement) &&
                !isOpen &&
                !hasTriggeredBlurRef.current
              ) {
                onBlur?.();
              }
            }, 100);
          }}
        >
          <div className="flex items-center justify-between flex-wrap gap-1">
            {/* <span className={!selectedOption ? "text-secondary-400" : ""}>
              {selectedOption?.label || placeholder}
            </span> */}
            {selectedOptions.length > 0 ? (
              multiple ? (
                <div className="flex flex-wrap gap-1">
                  {selectedOptions.map((opt) => (
                    <span
                      key={opt.value}
                      className="flex items-center gap-1 rounded bg-gray-300 text-primary-700 px-2 py-0.5 text-xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                      <XMarkIcon
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleRemove(opt.value)}
                      />
                    </span>
                  ))}
                </div>
              ) : (
                <span>{selectedOptions[0]?.label}</span>
              )
            ) : (
              <span className="text-secondary-400">{placeholder}</span>
            )}
            <ChevronUpDownIcon className="h-5 w-5 text-secondary-400" />
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full rounded-lg border border-secondary-200 bg-white shadow-lg">
            {searchable && (
              <div className="p-2 border-b border-secondary-200">
                <div className="relative">
                  <input
                    type="text"
                    className="w-full rounded-lg border border-secondary-200 px-3 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary-700"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-secondary-400" />
                </div>
              </div>
            )}

            <div className="max-h-60 overflow-y-auto py-1">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-secondary-500">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-4 py-2 text-sm ${
                      option.disabled
                        ? "cursor-not-allowed text-secondary-400"
                        : "cursor-pointer hover:bg-secondary-100"
                    } ${
                      option.value === value
                        ? "bg-primary-50 text-primary-700"
                        : "text-secondary-700"
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-1 text-sm text-red-600 animate-fade-in">
            {error.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Dropdown;
